"use client";

import NextLink, { LinkProps } from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import { FC } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import type { AnchorHTMLAttributes } from "react";

export type BaseLinkProps = Omit<LinkProps, "className"> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
    children?: React.ReactNode;
    className?: string;
  };

export const BaseLink: FC<BaseLinkProps> = (props) => {
  const pathname = usePathname();
  const { className, ...rest } = props;
  const segments =
    props.href.toString().split("#")[0]?.split("/") ||
    props.href.toString().split("/");
  const isActive = pathname.split("/").includes(segments[segments.length - 1]);

  return <NextLink {...rest} className={className} data-active={isActive} />;
};

export const Link: FC<
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
    LinkProps & {
      children?: React.ReactNode;
      className?: string;
    }
> = (props) => {
  return (
    <BaseLink
      {...props}
      className={cn(
        "text-primary hover:text-darken-primary/10 active:text-darken-primary/20 transition-all",
        props.className
      )}
    />
  );
};

export const LinkWithChevron = ({
  children,
  ...props
}: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps & { children?: React.ReactNode }) => {
  return (
    <Link
      {...props}
      className={cn(
        "group/link text-sm text-primary hover:text-darken-primary/10 active:text-darken-primary/20 transition-all",
        props.className
      )}
    >
      {children}
      <ChevronRight className="size-4 ml-0.5 group-hover/link:translate-x-0.5 transition-all inline-block" />
    </Link>
  );
};
